import { useDispatch, useSelector, useStore } from "react-redux";
import {
	confirmPeriodNotification as confirmPeriodNotificationAction,
	createGameContext as createGameContextAction,
	deleteGameContext as deleteGameContextAction,
	finalizeGame as finalizeGameAction,
	getBoxscoreJsonForGame as getBoxscoreJsonForGameAction,
	pushBoxscoreForGame as pushBoxscoreForGameAction,
	getEventDataForGame as getEventDataForGameAction,
	handlePeriodTransition as handlePeriodTransitionAction,
	keepSwappingTeams as keepSwappingTeamsAction,
	listLocalGames as listLocalGamesAction,
	rebuildDriveAssignmentsForGame as rebuildDriveAssignmentsForGameAction,
	pauseGameClock as pauseGameClockAction,
	resumeGameClock as resumeGameClockAction,
	orderPlayersByPosition as orderPlayersByPositionAction,
	rebuildDriveTimesForGame as rebuildDriveTimesForGameAction,
	saveFinalizationSettings as saveFinalizationSettingsAction,
	saveMatchesFinalizationOrder as saveMatchesFinalizationOrderAction,
	setOvertimeNotification as setOvertimeNotificationAction,
	setTimeAndPeriod as setTimeAndPeriodAction,
	showTimeConfig as showTimeConfigAction,
	swapTeams as swapTeamsAction,
	updateGameContext as updateGameContextAction,
	updateOffensiveTeam as updateOffensiveTeamAction
} from "./actions";
import { getSafe } from "../../util/object-util";
import { useAppContext } from "../app/context";

const useGameContextContext = () => {
	const dispatch = useDispatch();
	const store = useStore();
	const { redirect } = useAppContext();

	const useIsLoading = () =>
		useSelector(({ gameContext }) => gameContext.isLoading);

	const useGameClock = () =>
		useSelector(({ gameContext }) => gameContext.gameClock);

	const useIsPaused = () =>
		useSelector(({ gameContext }) =>
			getSafe(gameContext.gameClock.paused, false)
		);

	const useHomeTeamHasPossession = () =>
		useSelector(({ gameContext }) => gameContext.homeTeamHasPossession);

	const useRuleSetId = () =>
		useSelector(({ gameContext }) => gameContext.ruleSetId);

	const useHomeScore = () =>
		useSelector(({ gameContext }) => gameContext.homeScore);

	const useVisitorScore = () =>
		useSelector(({ gameContext }) => gameContext.visitorScore);

	const useYardsToFirstDown = () =>
		useSelector(({ gameContext }) => gameContext.yardsToFirstDown);

	const useGameConfig = () =>
		useSelector(({ gameContext }) => gameContext.gameConfig);

	const useFieldPosition = () =>
		useSelector(({ gameContext }) => gameContext.fieldPosition);

	const useFieldPositionFormatted = () =>
		useSelector(({ gameContext }) => gameContext.fieldPositionFormatted);

	const useFirstDownAt = () =>
		useSelector(({ gameContext }) => gameContext.firstDownAt);

	const useStartSettings = () =>
		useSelector(({ gameContext }) => gameContext.startSettings);

	const useDown = () => useSelector(({ gameContext }) => gameContext.down);

	const usePeriod = () =>
		useSelector(({ gameContext }) => {
			return gameContext.period;
		});

	const useOvertimePeriod = () =>
		useSelector(({ gameContext }) => {
			return gameContext.overtimePeriod;
		});

	const useOvertimeNotification = () =>
		useSelector(({ gameContext }) => {
			return gameContext.overtimeNotification;
		});

	const useIsGameInitialized = () =>
		useSelector(({ gameContext }) => gameContext.gameInitialized);

	const useFinalizeSettings = () =>
		useSelector(({ gameContext }) => gameContext.finalizeSettings);

	const useValidationErrors = () =>
		useSelector(({ gameContext }) => gameContext.validationErrors);

	const useShowTimeConfig = () =>
		useSelector(({ gameContext }) => gameContext.showTimeConfig);

	const useMissingGameData = () =>
		useSelector(({ gameContext }) => gameContext.missingGameData);

	const useHomeTimeouts = () =>
		useSelector(({ gameContext }) => gameContext.currentHomeTimeouts);

	const useVisitorTimeouts = () =>
		useSelector(({ gameContext }) => gameContext.currentVisitorTimeouts);

	const useSwapTeams = () =>
		useSelector(({ gameContext }) => gameContext.swapTeams);

	const useOrderPlayersByPosition = () =>
		useSelector(({ gameContext }) => gameContext.orderPlayersByPosition);

	const useKeepSwappingTeams = () =>
		useSelector(({ gameContext }) => gameContext.keepSwappingTeams);

	const useTriggerSwapTeams = () =>
		useSelector(({ gameContext }) => gameContext.triggerSwapTeams);

	const useTriggerNewPeriodNotification = () =>
		useSelector(({ gameContext }) => gameContext.triggerNewPeriodNotification);

	const useTriggerKO = () =>
		useSelector(({ gameContext }) => gameContext.triggerKO);

	const useTriggerPAT = () =>
		useSelector(({ gameContext }) => gameContext.triggerPAT);

	const useTriggerEndTime = () =>
		useSelector(({ gameContext }) => gameContext.triggerEndTime);

	const useFbOtDisabled = () =>
		useSelector(({ gameContext }) => gameContext.fbOtDisabled);

	const eventSettingsMessages = [
		{
			type: "info",
			message: "Cannot change event settings after game has started."
		}
	];

	const useEventSettingsMessages = () =>
		useSelector(({ gameContext, app }) => {
			if (app.allowEventSettingsEditing) {
				return [
					{
						type: "warning",
						message:
							"Edit event settings after game has started has been manually overridden."
					}
				];
			}

			return gameContext.gameInitialized ? eventSettingsMessages : [];
		});

	const useTeamPossession = () =>
		useSelector(({ gameContext, teams }) => {
			return gameContext.homeTeamHasPossession ? teams.homeTeam : teams.awayTeam;
		});

	const useWinnerTeamId = () =>
		useSelector(({ gameContext }) => gameContext.winnerTeamId);

	const useIsGameTimeOver = () =>
		useSelector(({ gameContext }) => {
			const { period, gameClock } = gameContext;
			const nowMs =
				gameClock && typeof gameClock.now === "function"
					? gameClock.now()
					: (gameClock && gameClock.now) || 0;
			return (period === 4 && nowMs === 0) || period > 4;
		});

	const usePromptReceivingTeam = () =>
		useSelector(({ gameContext }) => gameContext.promptReceivingTeam);

	// clockType (from core's gameContext, persisted in IndexedDB) is the
	// single source of truth for the running clock feature.
	const useRunningClockEnabled = () =>
		useSelector(({ gameContext }) => gameContext.clockType === "running");

	const state = {
		isLoading: useIsLoading,
		gameClock: useGameClock,
		isPaused: useIsPaused,
		homeTeamHasPossession: useHomeTeamHasPossession,
		homeScore: useHomeScore,
		visitorScore: useVisitorScore,
		homeTimeouts: useHomeTimeouts,
		visitorTimeouts: useVisitorTimeouts,
		yardsToFirstDown: useYardsToFirstDown,
		gameConfig: useGameConfig,
		fieldPosition: useFieldPosition,
		fieldPositionFormatted: useFieldPositionFormatted,
		firstDownAt: useFirstDownAt,
		down: useDown,
		period: usePeriod,
		startSettings: useStartSettings,
		isGameInitialized: useIsGameInitialized,
		finalizeSettings: useFinalizeSettings,
		eventSettingsMessages: useEventSettingsMessages,
		validationErrors: useValidationErrors,
		showTimeConfig: useShowTimeConfig,
		missingGameData: useMissingGameData,
		teamPossession: useTeamPossession,
		overtimePeriod: useOvertimePeriod,
		overtimeNotification: useOvertimeNotification,
		swapTeams: useSwapTeams,
		orderPlayersByPosition: useOrderPlayersByPosition,
		keepSwappingTeams: useKeepSwappingTeams,
		triggerSwapTeams: useTriggerSwapTeams,
		triggerNewPeriodNotification: useTriggerNewPeriodNotification,
		winnerTeamId: useWinnerTeamId,
		triggerKO: useTriggerKO,
		triggerPAT: useTriggerPAT,
		triggerEndTime: useTriggerEndTime,
		isGameTimeOver: useIsGameTimeOver,
		fbOtDisabled: useFbOtDisabled,
		ruleSetId: useRuleSetId,
		promptReceivingTeam: usePromptReceivingTeam,
		runningClockEnabled: useRunningClockEnabled
	};

	const updateGameContext = (gameContext) => {
		updateGameContextAction(gameContext, dispatch);
	};

	const createGameContext = (gameContext) => {
		createGameContextAction(gameContext, dispatch);
	};

	const deleteGameContext = () => {
		const { gameContext } = store.getState();
		deleteGameContextAction(gameContext, dispatch);
	};

	const finalizeGame = (values) => {
		const { gameContext } = store.getState();
		const { gameId } = gameContext;
		const { currentSeason } = store.getState().season;

		finalizeGameAction(currentSeason.id, gameId, values, redirect, dispatch);
	};

	const saveFinalizationSettings = (values) => {
		const { gameContext } = store.getState();
		const { gameId } = gameContext;

		saveFinalizationSettingsAction(gameId, values, redirect, dispatch);
	};

	const setTimeAndPeriod = ({ seconds, period }) => {
		const { gameContext } = store.getState();
		setTimeAndPeriodAction(seconds, period, gameContext, dispatch);
	};

	const showTimeConfig = (value) => {
		showTimeConfigAction(value, dispatch);
	};

	const setOvertimeNotification = (value) => {
		const { gameContext } = store.getState();
		const { gameId } = gameContext;
		setOvertimeNotificationAction(gameId, value, dispatch);
	};

	const swapTeams = (value) => {
		const { gameContext } = store.getState();
		const { gameId } = gameContext;
		return swapTeamsAction(gameId, value, dispatch);
	};

	const orderPlayersByPosition = (value) => {
		const { gameContext } = store.getState();
		const { gameId } = gameContext;
		orderPlayersByPositionAction(gameId, value, dispatch);
	};

	const keepSwappingTeams = (value) => {
		const { gameContext } = store.getState();
		const { gameId } = gameContext;
		keepSwappingTeamsAction(gameId, value, dispatch);
	};

	const confirmPeriodNotification = () => {
		const { gameContext } = store.getState();
		const { gameId } = gameContext;
		confirmPeriodNotificationAction(gameId, dispatch);
	};

	const saveMatchesFinalizationOrder = async (isSingles, orderedIds) => {
		const { gameContext } = store.getState();
		const { gameId } = gameContext;
		saveMatchesFinalizationOrderAction(gameId, isSingles, orderedIds, dispatch);
	};

	const handlePeriodTransition = () => {
		const { gameContext } = store.getState();
		const { gameId } = gameContext;
		handlePeriodTransitionAction(gameId, dispatch);
	};

	const updateOffensiveTeam = async (teamId) => {
		const { gameContext } = store.getState();
		const { gameId } = gameContext;
		updateOffensiveTeamAction(gameId, teamId, dispatch);
	};

	const pauseGameClock = (options) => {
		const { gameContext } = store.getState();
		return pauseGameClockAction(gameContext.gameId, dispatch, options);
	};

	const resumeGameClock = (options) => {
		const { gameContext } = store.getState();
		return resumeGameClockAction(gameContext.gameId, dispatch, options);
	};

	const rebuildDriveTimesForGame = (gameId) =>
		rebuildDriveTimesForGameAction(gameId);

	const listLocalGames = () => listLocalGamesAction();

	const rebuildDriveAssignmentsForGame = (gameId) =>
		rebuildDriveAssignmentsForGameAction(gameId);

	const getBoxscoreJsonForGame = (gameId) =>
		getBoxscoreJsonForGameAction(gameId);

	const pushBoxscoreForGame = (gameId) => pushBoxscoreForGameAction(gameId);

	const getEventDataForGame = (gameId) => getEventDataForGameAction(gameId);

	return {
		state,
		createGameContext,
		updateGameContext,
		deleteGameContext,
		setTimeAndPeriod,
		finalizeGame,
		showTimeConfig,
		setOvertimeNotification,
		swapTeams,
		orderPlayersByPosition,
		keepSwappingTeams,
		confirmPeriodNotification,
		saveMatchesFinalizationOrder,
		saveFinalizationSettings,
		handlePeriodTransition,
		updateOffensiveTeam,
		rebuildDriveTimesForGame,
		listLocalGames,
		rebuildDriveAssignmentsForGame,
		getBoxscoreJsonForGame,
		pushBoxscoreForGame,
		getEventDataForGame,
		pauseGameClock,
		resumeGameClock
	};
};

export { useGameContextContext };
